import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Type-to-search single-select. Drop-in replacement for shadcn Select when
 * the option list is large (300+ SKUs, 300+ distributors).
 *
 * Props
 * -----
 *   value            selected id (string) or ""
 *   onChange(id)     called with the new id
 *   options          array of { id, code, name, label? } (all optional
 *                    except id); label overrides the default `code — name`
 *   placeholder      trigger placeholder
 *   emptyMessage     shown when the filter has no match
 *   testid           data-testid on the trigger
 */
export function SearchableSelect({
  value, onChange, options = [],
  placeholder = "Select…",
  emptyMessage = "No match.",
  testid,
  disabled = false,
  clearable = true,
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((o) => o.id === value);

  const renderLine = (o) => o.label || [o.code, o.name].filter(Boolean).join(" — ") || o.id;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button" variant="outline" role="combobox" disabled={disabled}
          aria-expanded={open}
          className={cn("w-full justify-between font-normal", className)}
          data-testid={testid}
        >
          <span className="truncate text-left">
            {selected ? renderLine(selected) : <span className="text-neutral-500">{placeholder}</span>}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* Content width follows the trigger but never shrinks below a
          readable minimum — triggers used in tight grid layouts (e.g. a
          compact SKU-row picker, or a Zone/Sub-Zone/Distributor/SKU filter
          row) are often much narrower than a typical option label, and some
          SKU names run considerably longer than "FG00014 — TULSI CLASSIC
          BROKENS TEA 500G" (e.g. "...FANNINGS TEA 250G(SCP)(24KG)"). 320px
          still truncated those to unreadable fragments — 420px plus letting
          the label wrap (see the CommandItem span below) instead of
          ellipsizing fixes it for the realistic worst case, not just the
          common one. */}
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[420px] p-0" align="start">
        <Command
          shouldFilter
          filter={(itemValue, search) => {
            // itemValue is the CommandItem's value (we set it to id below).
            // We also stash a `data-search` string containing code + name + label
            // for matching against the search term.
            const opt = options.find((o) => o.id === itemValue);
            if (!opt) return 0;
            const hay = [opt.id, opt.code, opt.name, opt.label]
              .filter(Boolean).join(" ").toLowerCase();
            return hay.includes(String(search).toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Type to search…" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandList className="max-h-64">
            {clearable && (
              <CommandItem
                value=""
                onSelect={() => { onChange(""); setOpen(false); }}
                data-testid={testid ? `${testid}-clear` : undefined}
                className="text-neutral-500"
              >
                <span className="w-4 mr-2" /> Clear selection
              </CommandItem>
            )}
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.id}
                  value={o.id}
                  onSelect={() => { onChange(o.id); setOpen(false); }}
                  data-testid={testid ? `${testid}-opt-${o.id}` : undefined}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0",
                    value === o.id ? "opacity-100" : "opacity-0")} />
                  <span className="whitespace-normal leading-snug">{renderLine(o)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableSelect;
